import { useState } from 'react';
import type { Message } from '../../types';
import { formatTime } from '../../lib/utils';
import { Avatar } from '../design-system';

interface MessageBubbleProps {
  message: Message;
  onReact: (messageIndex: number, emoji: string) => void;
  index: number;
  reaction?: string;
  assistantAvatarUrl?: string | null;
  assistantName?: string;
  userAvatarUrl?: string | null;
}

const REACTION_EMOJIS = ['❤️', '👍', '😮', '😂', '🙏', '✨'];

function AlmaAvatar({ avatarUrl, name }: { avatarUrl?: string | null; name?: string }) {
  return (
    <div className="shrink-0 self-end mb-5">
      <Avatar kind="alma" src={avatarUrl ?? undefined} name={name} size={32} />
    </div>
  );
}

function renderContent(content: string) {
  const paragraphs = content.split('\n\n');
  return paragraphs.map((para, pi) => (
    <p key={pi} className={pi < paragraphs.length - 1 ? 'mb-2' : ''}>
      {para.split('\n').map((line, li, arr) => (
        <span key={li}>
          {line}
          {li < arr.length - 1 && <br />}
        </span>
      ))}
    </p>
  ));
}

function UserAvatar({ avatarUrl }: { avatarUrl?: string | null }) {
  if (!avatarUrl) return null;
  return (
    <div className="shrink-0 self-end mb-5">
      <Avatar kind="photo" src={avatarUrl} size={32} />
    </div>
  );
}

export function MessageBubble({ message, onReact, index, reaction, assistantAvatarUrl, assistantName, userAvatarUrl }: MessageBubbleProps) {
  const isUser = message.role === 'user';
  const [pickerVisible, setPickerVisible] = useState(false);

  function handleReact(emoji: string) {
    onReact(index, emoji);
    setPickerVisible(false);
  }

  return (
    <div className={`flex flex-col gap-0.5 ${isUser ? 'items-end' : 'items-start'}`}>
      <div className={`flex items-end gap-1.5 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
        {/* Avatars */}
        {!isUser && <AlmaAvatar avatarUrl={assistantAvatarUrl} name={assistantName} />}
        {isUser && <UserAvatar avatarUrl={userAvatarUrl} />}

        {/* Bubble + picker wrapper */}
        <div
          className="relative group max-w-[75%]"
          onMouseEnter={() => setPickerVisible(true)}
          onMouseLeave={() => setPickerVisible(false)}
        >
          {/* Reaction picker — shown on hover (desktop).
              Outer wrapper uses pb-1.5 padding (not margin) so the visual gap
              between bubble and picker stays inside the hoverable area —
              otherwise the mouse crosses a dead zone and the picker vanishes. */}
          {pickerVisible && (
            <div
              className={`absolute bottom-full z-10 pb-1.5 ${isUser ? 'right-0' : 'left-0'}`}
            >
              <div className="bg-app-card border border-app-border rounded-full shadow-lg flex gap-0.5 px-2 py-1">
                {REACTION_EMOJIS.map((emoji) => (
                  <button
                    key={emoji}
                    onClick={() => handleReact(emoji)}
                    className={`sk-hit 
                      w-7 h-7 flex items-center justify-center rounded-full text-base
                      hover:bg-app-bg active:scale-90 transition-transform
                      ${reaction === emoji ? 'bg-gold/10' : ''}
                    `}
                    aria-label={`React with ${emoji}`}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Message bubble — styled to the design system's chat/MessageBubble token
              treatment: 'you' is a warm neutral fill (var(--surface)), NEVER gold;
              'other' is transparent (var(--bg)) with a hairline border. The tail
              corner (radius 4 vs 16) points to the speaker, at the bottom. */}
          <div
            className="break-words [overflow-wrap:anywhere]"
            style={{
              fontSize: 'var(--chat-fs)',
              lineHeight: 'var(--chat-lh)',
              padding: 'var(--chat-bubble-pad-y) var(--chat-bubble-pad-x)',
              color: 'var(--text)',
              background: isUser ? 'var(--surface)' : 'var(--bg)',
              border: '1px solid var(--line)',
              borderRadius: 16,
              borderBottomRightRadius: isUser ? 4 : 16,
              borderBottomLeftRadius: isUser ? 16 : 4,
            }}
          >
            {renderContent(message.content)}
            <div className="mt-1 select-none text-right" style={{ color: 'var(--text-dim)', fontSize: 'var(--chat-fs-time)' }}>
              {formatTime(message.timestamp)}
            </div>
          </div>
        </div>

        {/* Mobile "+" reaction trigger */}
        <button
          onClick={() => setPickerVisible((v) => !v)}
          className="sm:hidden w-9 h-9 -mx-1 rounded-full text-app-muted text-base flex items-center justify-center self-center shrink-0 active:bg-app-bg sk-hit"
          aria-label="Add reaction"
        >
          <span className="w-5 h-5 rounded-full bg-app-bg flex items-center justify-center text-xs">+</span>
        </button>
      </div>

      {/* Reaction pill */}
      {reaction && (
        <div className={`${isUser ? 'mr-2' : 'ml-11'}`}>
          <button
            onClick={() => onReact(index, reaction)}
            className="bg-app-card border border-app-border rounded-full px-2 py-0.5 text-xs shadow-sm hover:bg-app-bg active:scale-95 transition-transform sk-hit"
            aria-label={`Remove ${reaction} reaction`}
          >
            {reaction}
          </button>
        </div>
      )}
    </div>
  );
}

export default MessageBubble;
